import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';

const SESSION_ERRORS = [
  'login_required',
  'consent_required',
  'interaction_required',
];

// An Auth0 error that means "a person has to sign in again": the session has expired, or, on
// localhost, consent has to be given again.
export const isSessionError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  SESSION_ERRORS.includes(String((error as { error?: unknown }).error));

// Every request asks Auth0 for a token first. When Auth0 can no longer mint one silently, every
// request fails the same way and the app would sit on a spinner for ever. Send the person to
// sign in instead, once, and bring them back to the page they were on.
export default function useSessionGuard() {
  const queryClient = useQueryClient();
  const { loginWithRedirect } = useAuth0();
  const { pathname, search } = useLocation();
  const redirecting = useRef(false);

  useEffect(() => {
    const handle = (error: unknown) => {
      if (redirecting.current || !isSessionError(error)) return;
      redirecting.current = true;
      loginWithRedirect({ appState: { returnTo: `${pathname}${search}` } });
    };
    const unsubscribeQueries = queryClient
      .getQueryCache()
      .subscribe((event) => {
        if (
          event?.type === 'queryUpdated' &&
          event.query.state.status === 'error'
        ) {
          handle(event.query.state.error);
        }
      });
    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe((mutation) => {
        if (mutation?.state.status === 'error') handle(mutation.state.error);
      });
    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, loginWithRedirect, pathname, search]);
}
